import { CustomAttribute } from '@/modules/common/types/custom-attribute';
import { compareFormValues } from '@/utils/form-state.utils';

import {
  CreateCustomAttributeInput,
  UpdateCustomAttributeInput,
} from '../api/custom-attributes.api';
import { CustomAttributeRowFormData } from '../validations/custom-attribute-validation';

/**
 * Row/option ids are negative for unsaved drafts and positive for persisted records.
 * These predicates name that convention so call sites read intent, not raw sign checks.
 */
export const isDraftId = (id: number): boolean => id < 0;
export const isPersistedId = (id: number): boolean => id > 0;

/**
 * Maps loaded attribute domain models into the table form's row shape.
 */
export const mapAttributesToFormRows = (
  attributes: CustomAttribute[] | undefined
): CustomAttributeRowFormData[] =>
  (attributes ?? []).map(attr => ({
    attributeId: attr.id,
    name: attr.name,
    type: attr.type,
    options: (attr.options ?? []).map(option => ({ id: option.id, value: option.value })),
  }));

/**
 * The set of create/update requests needed to persist the table's current state.
 * Deletions are handled separately by the delete confirmation modal.
 */
export interface CustomAttributesSaveDiff {
  create: CreateCustomAttributeInput[];
  update: (UpdateCustomAttributeInput & { id: number })[];
}

/**
 * Builds the save diff by comparing the current rows against the loaded snapshot:
 * - draft rows (negative id) with a non-empty name become create requests
 * - persisted rows (positive id) whose name or options changed become update requests
 *
 * Existing options keep their backend id; draft options (negative id) omit it so the
 * backend creates them.
 */
export const buildCustomAttributesSaveDiff = (
  rows: CustomAttributeRowFormData[],
  initialRows: CustomAttributeRowFormData[]
): CustomAttributesSaveDiff => {
  const initialById = new Map(initialRows.map(row => [row.attributeId, row]));

  const create = rows
    .filter(row => isDraftId(row.attributeId) && row.name.trim().length > 0)
    .map(row => ({
      name: row.name.trim(),
      type: row.type,
      optionValues: row.options.map(option => option.value),
    }));

  const update = rows
    .filter(row => isPersistedId(row.attributeId))
    .filter(row => compareFormValues(row, initialById.get(row.attributeId)))
    .map(row => ({
      id: row.attributeId,
      name: row.name.trim(),
      selectOptions: row.options.map(option =>
        isPersistedId(option.id) ? { id: option.id, value: option.value } : { value: option.value }
      ),
    }));

  return { create, update };
};
